// packages/web/src/components/account/credits-hero.tsx
/**
 * CreditsHero — point balance display with recharge CTA.
 *
 * Visual: spec 附录 A · 印章学术 · navy gradient card with 朱砂 action button.
 * Shown in the account drawer (Phase 2) + avatar dropdown summary.
 * Uses tokens.css vars only (css-token-invariants enforced).
 */

import { useStore } from '../../lib/store/index.js';
import { useT } from '../../i18n/use-t.js';

export function CreditsHero({ onRecharge }: { onRecharge?: () => void }) {
  const t = useT();
  const balance = useStore((s) => s.balance);
  const loading = useStore((s) => s.balanceLoading);

  // Estimate courses from balance (1 full ingest ≈ 104 points · spec §2.1).
  const estimateCourses = balance !== null ? Math.floor(balance / 104) : 0;

  return (
    <div
      className="credits-hero"
      style={{
        background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-hover))',
        color: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-3xl)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ fontSize: 'var(--text-sm)', opacity: 0.8, marginBottom: 'var(--space-sm)' }}>
        {t('account.myPoints')}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: '44px',
          fontWeight: 700,
          lineHeight: 1,
          marginBottom: 'var(--space-sm)',
          letterSpacing: '-1px',
        }}
      >
        {loading ? '…' : (balance ?? 0).toLocaleString()}
        <span style={{ fontSize: 'var(--text-md)', opacity: 0.7, fontWeight: 400 }}> {t('account.points')}</span>
      </div>
      {balance !== null && (
        <div style={{ fontSize: 'var(--text-xs)', opacity: 0.65, marginTop: 'var(--space-sm)' }}>
          ≈ {estimateCourses} {t('account.coursesWorth')}
        </div>
      )}
      {onRecharge && (
        <button
          type="button"
          onClick={onRecharge}
          style={{
            marginTop: 'var(--space-xl)',
            padding: '9px 18px',
            background: 'var(--color-action)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-md)',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
            <circle cx="8" cy="8" r="6" />
            <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
            <path d="M7 6h1v4" />
          </svg>
          {t('account.recharge')}
        </button>
      )}
    </div>
  );
}
